// src/modules/cad/cad-reader.js

import {
    initCADDatabase
} from "./cad-db.js";

import {
    parseDXFStyles,
    getEntityStyle
} from "./dxf-style.js";


/**
 * ============================================================
 * DXF STYLE CACHE
 * ============================================================
 */

const styleCache = new Map();

/**
 * ============================================================
 * DXF ELEVATION CACHE
 * ============================================================
 */

const elevationCache = new Map();
const pointElevationCache = new Map();
const vertexCache = new Map();


/**
 * ============================================================
 * Parse Original DXF Z Values
 * ============================================================
 *
 * Mengambil Z langsung dari DXF asli.
 *
 * LINE:
 *   30 = Start Z
 *   31 = End Z
 *
 * POINT:
 *   30 = Z
 *
 * LWPOLYLINE:
 *   38 = elevation
 *
 * POLYLINE / VERTEX:
 *   30 = Z vertex
 * ============================================================
 */

async function parseDXFElevations(file) {

    const extension =
        String(file.name || "")
            .split(".")
            .pop()
            .toLowerCase();

    if (extension !== "dxf") {
        return null;
    }

    const text = await file.text();
    const lines = text.split(/\r?\n/);

    const elevations = new Map();
    const pointElevations = new Map();
    const vertexHandles = new Set();
    const vertices = [];

    const pairs = [];

    for (
        let i = 0;
        i + 1 < lines.length;
        i += 2
    ) {

        pairs.push([
            lines[i].trim(),
            lines[i + 1].trim()
        ]);

    }

    let i = 0;

    while (i < pairs.length) {

        if (pairs[i][0] !== "0") {
            i++;
            continue;
        }

        const entityType =
            pairs[i][1].toUpperCase();

        let j = i + 1;

        while (
            j < pairs.length &&
            pairs[j][0] !== "0"
        ) {
            j++;
        }

        const entity =
            pairs.slice(i + 1, j);

        let handle = null;
        let x = null;
        let y = null;
        let zStart = null;
        let zEnd = null;
        let elevation = null;
        let pointX = null;
        let pointY = null;

        for (const [code, value] of entity) {

            /*
             * Entity Handle
             */
            if (code === "5") {

                handle =
                    String(value)
                        .trim()
                        .toUpperCase();

            }

            // ==================================================
            // VERTEX X
            // ==================================================
            if (
                entityType === "VERTEX" &&
                code === "10"
            ) {
                const valueX = Number(value);

                if (Number.isFinite(valueX)) {
                    x = valueX;
                }
            }

            // ==================================================
            // VERTEX Y
            // ==================================================
            if (
                entityType === "VERTEX" &&
                code === "20"
            ) {
                const valueY = Number(value);

                if (Number.isFinite(valueY)) {
                    y = valueY;
                }
            }

            // ==================================================
            // POINT X
            // ==================================================
            if (
                entityType === "POINT" &&
                code === "10"
            ) {
                const valueX = Number(value);

                if (Number.isFinite(valueX)) {
                    x = valueX;
                }
            }


            // ==================================================
            // POINT Y
            // ==================================================
            if (
                entityType === "POINT" &&
                code === "20"
            ) {
                const valueY = Number(value);

                if (Number.isFinite(valueY)) {
                    y = valueY;
                }
            }

            /*
             * LINE Start Z
             */
            if (
                entityType === "LINE" &&
                code === "30"
            ) {

                const z = Number(value);

                if (Number.isFinite(z)) {
                    zStart = z;
                }

            }

            /*
             * LINE End Z
             */
            if (
                entityType === "LINE" &&
                code === "31"
            ) {

                const z = Number(value);

                if (Number.isFinite(z)) {
                    zEnd = z;
                }

            }

            /*
             * POINT Z
             */
            if (
                entityType === "POINT" &&
                code === "30"
            ) {

                const z = Number(value);

                if (Number.isFinite(z)) {
                    elevation = z;
                }

            }

            /*
             * LWPOLYLINE elevation
             */
            if (
                entityType === "LWPOLYLINE" &&
                code === "38"
            ) {

                const z = Number(value);

                if (Number.isFinite(z)) {
                    elevation = z;
                }

            }

            /*
             * ==================================================
             * POLYLINE VERTEX Z
             * ==================================================
             *
             * Inilah yang kita perlukan.
             *
             * Setiap VERTEX mempunyai:
             *
             * 10 = X
             * 20 = Y
             * 30 = Z
             */
            if (
                entityType === "VERTEX" &&
                code === "30"
            ) {

                const z = Number(value);

                if (Number.isFinite(z)) {
                    elevation = z;
                }

            }

        }

        /*
         * ======================================================
         * LINE
         * ======================================================
         */

        if (
            entityType === "LINE" &&
            handle
        ) {

            if (Number.isFinite(zStart)) {

                elevation = zStart;

            } else if (
                Number.isFinite(zEnd)
            ) {

                elevation = zEnd;

            }

        }

        /*
         * ======================================================
         * Simpan Z asli DXF
         * ======================================================
         */

        if (
            handle &&
            Number.isFinite(elevation)
        ) {

            elevations.set(
                handle,
                elevation
            );

        }

        // ======================================================
        // POINT ELEVATION FALLBACK
        // ======================================================
        // POINT pada DXF ini tidak mempunyai EntityHandle.
        // Karena itu gunakan X + Y sebagai key.

        if (
            entityType === "POINT" &&
            Number.isFinite(x) &&
            Number.isFinite(y) &&
            Number.isFinite(elevation)
        ) {

            const pointKey =
                `${x}|${y}`;

            pointElevations.set(
                pointKey,
                elevation
            );
        }

        /*
         * ======================================================
         * Tandai SEMUA VERTEX
         * ======================================================
         *
         * Tidak peduli vertex mempunyai Z
         * atau tidak.
         */
        if (
            entityType === "VERTEX" &&
            handle
        ) {
            vertexHandles.add(handle);

            const z =
                Number.isFinite(elevation) &&
                elevation !== -999999
                    ? elevation
                    : null;

            if (
                Number.isFinite(x) &&
                Number.isFinite(y)
            ) {
                vertices.push({
                    handle,
                    x,
                    y,
                    z
                });
            }
        }

        i = j;

    }

    console.log(
        "[CAD] Original DXF elevations:",
        elevations.size
    );

    console.log(
        "[CAD] DXF vertices:",
        vertexHandles.size
    );

    return {
        elevations,
        pointElevations,
        vertexHandles,
        vertices
    };
}

/**
 * ============================================================
 * Register CAD file
 * ============================================================
 */

export async function registerCADFile(file) {

    const {
        db,
        conn
    } = await initCADDatabase();

    const virtualPath =
        `cad_${Date.now()}_${file.name}`;

    console.log(
        "[CAD] Registering:",
        virtualPath
    );

    /**
     * --------------------------------------------------------
     * Register file ke DuckDB
     * --------------------------------------------------------
     */

    const buffer =
        new Uint8Array(
            await file.arrayBuffer()
        );

    await db.registerFileBuffer(
        virtualPath,
        buffer
    );

    /**
     * --------------------------------------------------------
     * Parse DXF style
     * --------------------------------------------------------
     */

    let styleInfo = null;
    let elevationInfo = null;

    const extension =
        String(file.name || "")
            .split(".")
            .pop()
            .toLowerCase();

    if (
        extension === "dxf"
    ) {

        try {

            console.log(
                "[CAD] Reading DXF styles..."
            );

            styleInfo =
                await parseDXFStyles(
                    file
                );

                try {

                    elevationInfo =
                        await parseDXFElevations(
                            file
                        );

                    elevationCache.set(
                        virtualPath,
                        elevationInfo?.elevations ?? new Map()
                    );

                    pointElevationCache.set(
                        virtualPath,
                        elevationInfo?.pointElevations ?? new Map()
                    );

                    vertexCache.set(
                        virtualPath,
                        elevationInfo?.vertices ?? []
                    );
                   console.log(
                        "[CAD] DXF elevations loaded:",
                        elevationInfo?.elevations?.size ?? 0
                    );

                    console.log(
                        "[CAD] DXF vertices loaded:",
                        elevationInfo?.vertices?.length ?? 0
                    );
                } catch (error) {

                    console.warn(
                        "[CAD] DXF elevation parser failed:",
                        error
                    );

                }

            styleCache.set(
                virtualPath,
                styleInfo
            );

            console.log(
                "[CAD] DXF styles loaded."
            );

        } catch (error) {

            console.warn(
                "[CAD] DXF style parser failed:",
                error
            );

        }
    }

    return {
        db,
        conn,
        virtualPath,
        styleInfo
    };
}


/**
 * ============================================================
 * Get CAD Layers
 * ============================================================
 */

export async function getCADLayers(file) {

    const {
        conn,
        virtualPath,
        styleInfo,
        elevationInfo
    } =
        await registerCADFile(
            file
        );

    /**
     * --------------------------------------------------------
     * STEP 1
     * --------------------------------------------------------
     */

    console.log(
        "[CAD] STEP 1: ST_Read_Meta()"
    );

    const metaResult =
        await conn.query(`
            SELECT *
            FROM ST_Read_Meta(
                '${escapeSQL(
                    virtualPath
                )}'
            )
        `);

    console.log(
        "[CAD] ST_Read_Meta() OK"
    );

    /**
     * --------------------------------------------------------
     * Convert Arrow rows
     * --------------------------------------------------------
     */

    const metadataRows =
        metaResult
            .toArray()
            .map(
                row => row.toJSON()
            );

    console.log(
        "[CAD] Metadata rows:",
        metadataRows.length
    );

    if (
        !metadataRows.length
    ) {

        return {
            file,
            virtualPath,
            metadata: null,
            layers: [],
            styleInfo,
            elevationInfo
        };

    }

    const metadata =
        metadataRows[0];

    console.log(
        "[CAD] Metadata:",
        metadata
    );


    /**
     * ========================================================
     * STEP 2
     * Extract drawing layers
     *
     * PENTING:
     *
     * Jangan menggunakan:
     *
     * Array.isArray(metadata.layers)
     *
     * karena DuckDB-WASM dapat mengembalikan
     * LIST / STRUCT melalui Arrow.
     *
     * Gunakan UNNEST() langsung di DuckDB.
     * ========================================================
     */

    console.log(
        "[CAD] STEP 2: Extract drawing layers..."
    );

    const layerResult =
        await conn.query(`
            SELECT
                layer.name AS layer_name,
                layer.geometry_fields AS geometry_fields
            FROM ST_Read_Meta(
                '${escapeSQL(
                    virtualPath
                )}'
            ),
            UNNEST(layers) AS t(layer)
        `);

    console.log(
        "[CAD] UNNEST(layers) OK"
    );

    /**
     * --------------------------------------------------------
     * Arrow → JS
     * --------------------------------------------------------
     */

    const layers =
        layerResult
            .toArray()
            .map(
                row => row.toJSON()
            );

    console.log(
        "[CAD] Found layers:",
        layers.length
    );

    console.table(
        layers.map(
            (layer, index) => ({
                index,

                name:
                    layer.layer_name ??
                    layer.name ??
                    `Layer ${index + 1}`,

                geometry_fields:
                    layer.geometry_fields
            })
        )
    );

    /**
     * --------------------------------------------------------
     * Return
     * --------------------------------------------------------
     */

    return {

        file,

        virtualPath,

        metadata,

        layers,

        styleInfo,
        elevationInfo

    };
}


/**
 * ============================================================
 * Read CAD Layer
 * ============================================================
 */

export async function readCADLayer(
    virtualPath,
    layerName,
    sourceCRS = null
) {

    const {
        conn
    } = await initCADDatabase();

    console.log(
        "[CAD] Reading layer:",
        layerName
    );

    const safePath =
        escapeSQL(
            virtualPath
        );

    const safeLayer =
        escapeSQL(
            layerName
        );


    /**
     * --------------------------------------------------------
     * DESCRIBE
     * --------------------------------------------------------
     */

    const describeResult =
        await conn.query(`
            DESCRIBE
            SELECT *
            FROM ST_Read(
                '${safePath}',
                layer := '${safeLayer}'
            )
        `);

    const columns =
        describeResult
            .toArray()
            .map(
                row => row.toJSON()
            );

    console.log(
        "[CAD] Columns:"
    );

    console.table(
        columns.map(
            column => ({
                name:
                    column.column_name,

                type:
                    column.column_type
            })
        )
    );


    /**
     * --------------------------------------------------------
     * Find geometry column
     * --------------------------------------------------------
     */

    const geometryColumn =
        columns.find(
            column =>
                String(
                    column.column_type
                )
                    .toUpperCase()
                    .includes(
                        "GEOMETRY"
                    )
        );

    if (
        !geometryColumn
    ) {

        throw new Error(
            "Geometry column tidak ditemukan pada CAD layer."
        );

    }

    const geomName =
        geometryColumn.column_name;

    console.log(
        "[CAD] Geometry column:",
        geomName
    );


    /**
     * --------------------------------------------------------
     * Geometry expression
     * --------------------------------------------------------
     *
     * Jika sourceCRS diberikan:
     *
     * projected CRS
     *       ↓
     * OGC:CRS84
     *
     * Jika null:
     *
     * geometry asli digunakan.
     */

    const geometryExpression =
        sourceCRS
            ? `
                ST_Transform(
                    "${escapeIdentifier(
                        geomName
                    )}",
                    '${escapeSQL(
                        sourceCRS
                    )}',
                    'OGC:CRS84',
                    true
                )
            `
            : `
                "${escapeIdentifier(
                    geomName
                )}"
            `;


    /**
     * --------------------------------------------------------
     * Read features
     * --------------------------------------------------------
     */

    console.log(
        "[CAD] STEP 5: Reading features"
    );

    const result =
        await conn.query(`
            SELECT
                *,

                ST_AsGeoJSON(
                    ${geometryExpression}
                ) AS __geometry_json,

                ST_AsGeoJSON(
                    "${escapeIdentifier(
                        geomName
                    )}"
                ) AS __cad_source_geometry_json

                FROM ST_Read(
                    '${safePath}',
                    layer := '${safeLayer}'
            )
        `);


    let rows =
        result
            .toArray()
            .map(
                row => row.toJSON()
            );

            console.log(
                "[CAD DEBUG] Sample rows:",
                rows
                    .filter(
                        row =>
                            row.EntityHandle !== undefined
                    )
                    .slice(0, 30)
                    .map(row => ({
                        EntityHandle: row.EntityHandle,
                        Geometry: row.__geometry_json,

                        elevation:
                            elevationCache
                                .get(virtualPath)
                                ?.get(
                                    String(row.EntityHandle)
                                        .toUpperCase()
                                ),

                        isVertex: false
                    }))
            );

// ============================================================
// ORIGINAL DXF ELEVATION
// ============================================================

const elevationInfo =
    elevationCache.get(
        virtualPath
    ) ?? new Map();

const pointElevationInfo =
    pointElevationCache.get(
        virtualPath
    ) ?? new Map();

const vertices =
    vertexCache.get(
        virtualPath
    ) ?? [];

    let transformedVertices = [];

// Apply elevation hanya untuk entity
// yang memang mempunyai elevation sendiri.
// Jangan mencoba mencocokkan VERTEX
// dengan EntityHandle parent 3DPolyline.

rows =
    rows.map(row => {

        const handle =
            row.EntityHandle
                ?.toString()
                .toUpperCase();

        let elevation =
            elevationInfo.get(
                handle
            );

        // ==================================================
        // POINT ELEVATION FALLBACK
        // ==================================================

        if (
            !Number.isFinite(elevation) &&
            row.__cad_source_geometry_json
        ) {

            try {

                const geometry =
                    JSON.parse(
                        row.__cad_source_geometry_json
                    );

                if (
                    geometry?.type === "Point" &&
                    Array.isArray(
                        geometry.coordinates
                    )
                ) {

                    const [
                        x,
                        y
                    ] =
                        geometry.coordinates;

                    const pointKey =
                        `${x}|${y}`;

                    const pointElevation =
                        pointElevationInfo.get(
                            pointKey
                        );

                    if (
                        Number.isFinite(
                            pointElevation
                        )
                    ) {

                        elevation =
                            pointElevation;
                    }
                }

            } catch (error) {

                console.warn(
                    "[CAD] POINT elevation lookup failed:",
                    error
                );
            }
        }

        return {
            ...row,

            __cad_elevation:
                Number.isFinite(elevation)
                    ? elevation
                    : null,

            __cad_is_vertex:
                false
        };
    });

    if (
    sourceCRS &&
    vertices.length
) {

    const values =
        vertices
            .filter(vertex =>
                Number.isFinite(vertex.x) &&
                Number.isFinite(vertex.y)
            )
            .map(vertex => `
                (
                    '${escapeSQL(vertex.handle)}',
                    ${vertex.x},
                    ${vertex.y},
                    ${Number.isFinite(vertex.z)
                        ? vertex.z
                        : 'NULL'}
                )
            `)
            .join(",");

    if (values) {

        const vertexResult =
            await conn.query(`
                SELECT
                    handle,
                    ST_AsGeoJSON(
                        ST_Transform(
                            ST_Point(
                            CAST(x AS DOUBLE),
                            CAST(y AS DOUBLE)
                        ),
                            '${escapeSQL(sourceCRS)}',
                            'OGC:CRS84',
                            true
                        )
                    ) AS geometry_json
                FROM (
                    VALUES
                        ${values}
                ) AS t(
                    handle,
                    x,
                    y,
                    z
                )
            `);

        transformedVertices =
            vertexResult
                .toArray()
                .map(row => {

            const geometry =
                JSON.parse(
                    row.geometry_json
                );

            const originalVertex =
                vertices.find(
                    vertex =>
                        vertex.handle
                            .toUpperCase() ===
                        String(row.handle)
                            .toUpperCase()
                );

            return {
                handle: String(
                    row.handle
                ),

                x: geometry.coordinates[0],
                y: geometry.coordinates[1],

                /*
                 * PENTING:
                 * Z berasal dari DXF asli,
                 * bukan dari hasil ST_Transform().
                 */
                z:
                    originalVertex?.z ?? null,

                geometry
            };
        });
    }
}

console.log(
    "[CAD] Original entity Z applied."
);

console.log(
    "[CAD] DXF vertex records:",
    vertices.length
);



    console.log(
        "[CAD] Original Z applied."
    );


    console.log(
        "[CAD] Features:",
        rows.length
    );


    /**
     * ========================================================
     * Apply DXF Style
     * ========================================================
     */

    const styleInfo =
        styleCache.get(
            virtualPath
        );

    if (
        styleInfo
    ) {

        console.log(
            "[CAD] Applying DXF colors..."
        );

        rows =
            rows.map(
                row => {

                    const style =
                        getEntityStyle(
                            styleInfo,
                            row.EntityHandle
                        );


                    /**
                     * ------------------------------------------------
                     * Color
                     * ------------------------------------------------
                     */

                    row.__cad_color =
                        style?.color ??
                        "#222222";


                    /**
                     * ------------------------------------------------
                     * Color source
                     * ------------------------------------------------
                     */

                    row.__cad_color_source =
                        style?.colorSource ??
                        "default";


                    /**
                     * ------------------------------------------------
                     * ACI
                     * ------------------------------------------------
                     */

                    row.__cad_aci =
                        style?.aciColor ??
                        null;


                    /**
                     * ------------------------------------------------
                     * True Color
                     * ------------------------------------------------
                     */

                    row.__cad_true_color =
                        style?.trueColor ??
                        null;


                    /**
                     * ------------------------------------------------
                     * Layer Color
                     * ------------------------------------------------
                     */

                    row.__cad_layer_color =
                        style?.layerAciColor ??
                        null;


                    return row;

                }
            );


        /**
         * ----------------------------------------------------
         * Color statistics
         * ----------------------------------------------------
         */

        const colorStats = {};


        for (
            const row of rows
        ) {

            const color =
                row.__cad_color ??
                "#222222";

            colorStats[color] =
                (
                    colorStats[color] ||
                    0
                ) + 1;

        }


        console.log(
            "[CAD] Resolved colors:"
        );

        console.table(
            Object.entries(
                colorStats
            )
                .map(
                    ([color, count]) => ({
                        color,
                        count
                    })
                )
        );

    } else {

        console.warn(
            "[CAD] DXF style information not available."
        );

        rows =
            rows.map(
                row => ({
                    ...row,

                    __cad_color:
                        "#222222",

                    __cad_color_source:
                        "default"
                })
            );

    }


    /**
     * --------------------------------------------------------
     * First feature
     * --------------------------------------------------------
     */

    if (
        rows.length
    ) {

        console.log(
            "[CAD] First feature:",
            rows[0]
        );

    }


    return {
        layerName,
        geometryColumn: geomName,
        columns,
        rows,
        vertices: transformedVertices,
        styleInfo
    };
}


/**
 * ============================================================
 * Analyze CAD Features
 * ============================================================
 */

export function analyzeCADFeatures(
    rows
) {

    const stats = {};

    let minX =
        Infinity;

    let minY =
        Infinity;

    let maxX =
        -Infinity;

    let maxY =
        -Infinity;


    for (
        const row of rows
    ) {

        if (
            !row.__geometry_json
        ) {
            continue;
        }


        let geometry;


        try {

            geometry =
                typeof row.__geometry_json === "string"
                    ? JSON.parse(
                        row.__geometry_json
                    )
                    : row.__geometry_json;

        } catch (
            error
        ) {

            console.warn(
                "[CAD] Invalid geometry:",
                row.__geometry_json
            );

            continue;

        }


        if (
            !geometry
        ) {
            continue;
        }


        const type =
            geometry.type ||
            "Unknown";


        stats[type] =
            (
                stats[type] ||
                0
            ) + 1;


        collectCoordinates(
            geometry.coordinates,
            coordinate => {

                const x =
                    Number(
                        coordinate[0]
                    );

                const y =
                    Number(
                        coordinate[1]
                    );


                if (
                    !Number.isFinite(x) ||
                    !Number.isFinite(y)
                ) {
                    return;
                }


                minX =
                    Math.min(
                        minX,
                        x
                    );

                maxX =
                    Math.max(
                        maxX,
                        x
                    );

                minY =
                    Math.min(
                        minY,
                        y
                    );

                maxY =
                    Math.max(
                        maxY,
                        y
                    );

            }
        );

    }


    const hasExtent =
        Number.isFinite(minX) &&
        Number.isFinite(minY) &&
        Number.isFinite(maxX) &&
        Number.isFinite(maxY);


    const extent =
        hasExtent
            ? {

                minX,

                minY,

                maxX,

                maxY,

                width:
                    maxX - minX,

                height:
                    maxY - minY

            }
            : null;


    console.log(
        "[CAD] Geometry types:"
    );

    console.table(
        stats
    );


    console.log(
        "[CAD] Extent:"
    );

    console.table(
        extent
    );


    return {
        stats,
        extent
    };
}


/**
 * ============================================================
 * Coordinate iterator
 * ============================================================
 */

function collectCoordinates(
    coordinates,
    callback
) {

    if (
        !Array.isArray(
            coordinates
        )
    ) {
        return;
    }


    if (
        coordinates.length >= 2 &&
        typeof coordinates[0] === "number" &&
        typeof coordinates[1] === "number"
    ) {

        callback(
            coordinates
        );

        return;
    }


    for (
        const item
        of coordinates
    ) {

        collectCoordinates(
            item,
            callback
        );

    }
}


/**
 * ============================================================
 * SQL string escaping
 * ============================================================
 */

function escapeSQL(
    value
) {

    return String(value)
        .replace(
            /\\/g,
            "\\\\"
        )
        .replace(
            /'/g,
            "''"
        );

}


/**
 * ============================================================
 * SQL identifier escaping
 * ============================================================
 */

function escapeIdentifier(
    value
) {

    return String(value)
        .replace(
            /"/g,
            '""'
        );

}